import type { ValueOf } from '#layers/base/public';
import type { ExtensionPermission } from '#layers/extensions/types/catalog';
import * as z from 'zod/v4-mini';
import { ErrorCodes, hasErrorCode } from '#layers/base/public';

/** The capabilities the Engine grants, written `<capability>:<value>` in a permission. */
export const PermissionCapability = {
	Net: 'net',
} as const;

export type PermissionCapability = ValueOf<typeof PermissionCapability>;

export interface ParsedPermission {
	/** Absent for a capability this Studio does not know, which is then shown as written. */
	capability?: PermissionCapability;
	value: string;
}

const capabilities = new Set<string>(Object.values(PermissionCapability));

function isPermissionCapability(value: string): value is PermissionCapability {
	return capabilities.has(value);
}

export function parsePermission(permission: string): ParsedPermission {
	const separator = permission.indexOf(':');
	const capability = permission.slice(0, separator);

	if (separator === -1 || !isPermissionCapability(capability)) return { value: permission };

	return { capability, value: permission.slice(separator + 1) };
}

export interface PermissionGroup {
	/** Absent for the permissions whose capability this Studio does not know. */
	capability?: PermissionCapability;
	permissions: ExtensionPermission[];
}

/**
 * Groups permissions by capability, in the order `PermissionCapability` lists them, with any this
 * Studio does not know last. Order within a group is kept.
 */
export function groupPermissions(permissions: ExtensionPermission[]): PermissionGroup[] {
	const known = Object.values(PermissionCapability).map((capability): PermissionGroup => ({
		capability,
		permissions: permissions.filter(({ permission }) => parsePermission(permission).capability === capability),
	}));
	const unknown: PermissionGroup = {
		permissions: permissions.filter(({ permission }) => !parsePermission(permission).capability),
	};

	return [...known, unknown].filter((group) => group.permissions.length > 0);
}

const permissionsMismatchSchema = z.object({
	data: z.object({
		meta: z.object({
			permissions: z.array(z.object({
				permission: z.string(),
				reason: z.string(),
			})),
		}),
	}),
});

/**
 * The whole set a request has to grant when the Engine refused it for permissions it did not grant,
 * and `undefined` for any other failure. Resending the request with exactly this set satisfies it.
 */
export function getPermissionsToApproveFromError(error: unknown): ExtensionPermission[] | undefined {
	if (!hasErrorCode(error, ErrorCodes.EXTENSION_PERMISSIONS_MISMATCH)) return;

	const result = permissionsMismatchSchema.safeParse(error);

	return result.success ? result.data.data.meta.permissions : undefined;
}

/** Whether every permission in `required` is in `granted`. Reasons are not compared, as the Engine does not. */
export function coversRequiredPermissions(granted: ExtensionPermission[], required: ExtensionPermission[]): boolean {
	const grantedPermissions = new Set(granted.map(({ permission }) => permission));

	return required.every(({ permission }) => grantedPermissions.has(permission));
}
